/**
 * Operator Feedback Analytics Hooks (Phase 21.9.4)
 *
 * TanStack Query hooks for the manager-side feedback analytics page.
 * Fetches paginated feedback with filters.
 */

import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

// ============================================
// QUERY KEYS
// ============================================

export const feedbackAnalyticsKeys = {
  all: ['feedback-analytics'] as const,
  list: (params: FeedbackListParams) =>
    [...feedbackAnalyticsKeys.all, 'list', params] as const,
};

// ============================================
// TYPES
// ============================================

export interface FeedbackListParams {
  page?: number;
  limit?: number;
  feedbackType?: 'assessment_fairness' | 'training_rating' | '';
  sentiment?: 'positive' | 'negative' | '';
}

export interface FeedbackListItem {
  id: string;
  feedbackType: 'assessment_fairness' | 'training_rating';
  targetId: string;
  sentiment: 'positive' | 'negative';
  comment: string | null;
  isAnonymous: boolean;
  createdAt: string;
  operatorId: string | null;
  operatorName: string | null;
  context: string;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ============================================
// QUERIES
// ============================================

/**
 * Fetch paginated operator feedback for the analytics page.
 */
export function useOperatorFeedbackList(params: FeedbackListParams = {}) {
  const api = useApi();
  const { page = 1, limit = 20, feedbackType, sentiment } = params;

  return useQuery({
    queryKey: feedbackAnalyticsKeys.list({ page, limit, feedbackType, sentiment }),
    queryFn: () => {
      const searchParams = new URLSearchParams();
      searchParams.set('page', String(page));
      searchParams.set('limit', String(limit));
      if (feedbackType) searchParams.set('feedbackType', feedbackType);
      if (sentiment) searchParams.set('sentiment', sentiment);

      return api.get<PaginatedResponse<FeedbackListItem>>(
        `/api/dashboard/operator-feedback?${searchParams.toString()}`
      );
    },
    refetchInterval: 5 * 60000, // Refresh every 5 minutes
  });
}
